import React, { useState } from 'react';
import DisasterMessageCard from './DisasterMessageCard';
import './DisasterMessagesList.css';

const DisasterMessagesList = ({ messages, loading, error }) => {
  const [selectedMessage, setSelectedMessage] = useState(null);

  // API 응답 구조 처리 함수
  const processMessages = (data) => {
    console.log('DisasterMessagesList - processMessages 호출:', data);

    if (!data) return [];

    // 배열이지만 내부에 객체가 있고 그 객체에 body 배열이 있는 경우 처리
    if (Array.isArray(data) && data.length === 1 && data[0] && data[0].body && Array.isArray(data[0].body)) {
      console.log('배열 내부에 body 배열 구조 발견. 길이:', data[0].body.length);
      return data[0].body;
    }

    // 객체이고 body 배열이 있는 경우
    if (data && data.body && Array.isArray(data.body)) {
      console.log('객체 내부에 body 배열 구조 발견. 길이:', data.body.length);
      return data.body;
    }

    // 이미 배열인 경우
    if (Array.isArray(data)) {
      console.log('이미 배열 형태임. 길이:', data.length);
      return data;
    }

    // 그 외 경우는 원본 반환
    console.log('처리할 수 없는 데이터 구조. 원본 반환');
    return data;
  };

  // 처리된 메시지 배열
  const processedMessages = processMessages(messages);
  console.log('처리된 메시지 배열 길이:', processedMessages.length);

  // 재난문자 상세 정보 표시 핸들러
  const handleMessageClick = (message) => {
    console.log('상세 정보 메시지 클릭됨:', message);
    setSelectedMessage(message);
  };

  // 상세 정보 모달 닫기 핸들러
  const handleCloseDetails = () => {
    setSelectedMessage(null);
  };

  // 날짜 형식 변환 함수 (YYYYMMDD -> YYYY-MM-DD)
  const formatDate = (dateStr) => {
    if (!dateStr) return '정보 없음';

    // YYYYMMDD 형식 변환
    if (dateStr.length === 8) {
      return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
    }

    // CRT_DT 형식 (2023/09/16 11:09:49) 처리
    if (dateStr.includes('/')) {
      return dateStr;
    }

    // 이미 정상적인 날짜 형식이면 그대로 반환
    return dateStr;
  };

  // 메시지에서 발신 기관 추출하는 함수
  const extractAgency = (message) => {
    if (!message) return '';

    const matches = message.match(/\[(.*?)\]/);
    if (matches && matches.length > 1) {
      return matches[1];
    }

    return '';
  };

  // 실종자 관련 메시지인지 확인하는 함수
  const isMissingPersonMessage = (item) => {
    if (!item) return false;

    const message = (item.MSG_CN || item.msg || '').toLowerCase();

    // 발신 기관 추출
    const agencyMatch = message.match(/\[(.*?)\]/);
    const agency = agencyMatch && agencyMatch.length > 1 ? agencyMatch[1].toLowerCase() : '';

    // 경찰청 또는 경찰서 발송 메시지
    const isPoliceAgency = agency.includes('경찰청') || agency.includes('경찰서');

    // 실종자 관련 키워드
    const missingKeywords = ['실종', '미아', '찾습니다', '행방불명', '발견되었습니다', '사람을 찾습니다', '세(남)', '세(여)'];

    // 실종자 키워드 포함 여부
    const containsMissingKeywords = missingKeywords.some(keyword => message.includes(keyword.toLowerCase()));

    // 실종자 정보 패턴 (나이+성별 패턴)
    const hasAgeGenderPattern = /\d+세.*[남|여]/.test(message) ||
      /\(남,.*세\)/.test(message) ||
      /\(여,.*세\)/.test(message);

    // 실종자 메시지로 판단하는 조건
    return (isPoliceAgency && containsMissingKeywords) ||
      (containsMissingKeywords && hasAgeGenderPattern);
  };

  // 디버깅: 메시지 데이터 분석
  console.log('DisasterMessagesList에 전달된 메시지 개수:', Array.isArray(messages) ? messages.length : 'Not an array');
  if (Array.isArray(messages) && messages.length > 0) {
    console.log('첫 번째 메시지 예시:', messages[0]);
  }

  if (loading && messages.length === 0) {
    return <div className="loading">재난문자 정보를 불러오는 중...</div>;
  }

  if (error && messages.length === 0) {
    return <div className="error-message">{error}</div>;
  }

  if (messages.length === 0) {
    return (
      <div className="no-results">
        <h3>검색 결과가 없습니다</h3>
        <p>다른 검색어로 다시 시도하거나 검색 조건을 변경해 보세요.</p>
        <p>실종자 관련 메시지를 보려면 조회일자에 '20250331'을 입력하세요.</p>
      </div>
    );
  }

  return (
    <div className="disaster-messages-list">
      <div className="messages-grid">
        {processedMessages.map((message, index) => (
          <DisasterMessageCard
            key={message.SN || message.seq || index}
            message={message}
            onClick={handleMessageClick}
            isMissingPerson={isMissingPersonMessage(message)}
          />
        ))}
      </div>

      {/* 재난문자 상세 정보 모달 */}
      {selectedMessage && (
        <div className="message-details-modal">
          <div className="modal-overlay" onClick={handleCloseDetails}></div>
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">
                재난문자 상세 정보
                {isMissingPersonMessage(selectedMessage) && (
                  <span className="missing-badge">실종자 정보</span>
                )}
              </h3>
              <button className="close-button" onClick={handleCloseDetails}>×</button>
            </div>

            <div className="modal-body">
              <div className="message-info">
                <div className="message-header-info">
                  <p>
                    <strong>발신 기관:</strong> {extractAgency(selectedMessage.MSG_CN || selectedMessage.msg || '')}
                  </p>
                  <p>
                    <strong>발송 일시:</strong> {formatDate(selectedMessage.CRT_DT || selectedMessage.create_date || '')}
                  </p>
                  <p>
                    <strong>지역:</strong> {selectedMessage.RCPTN_RGN_NM || selectedMessage.areaName || '정보 없음'}
                  </p>
                  <p>
                    <strong>재난 유형:</strong> {selectedMessage.DST_SE_NM || selectedMessage.type || '정보 없음'}
                  </p>
                </div>

                <div className="message-content">
                  <h4>메시지 내용</h4>
                  <p className="message-text">
                    {selectedMessage.MSG_CN || selectedMessage.msg || '내용 없음'}
                  </p>
                </div>

                {isMissingPersonMessage(selectedMessage) && (
                  <div className="missing-person-info">
                    <h4>실종자 관련 정보</h4>
                    <p>
                      이 문자는 실종자 찾기 알림으로 확인되었습니다. 실종자를 발견하신 경우 아래 연락처로 신고해 주세요.
                    </p>
                    <ul className="contact-info">
                      <li><strong>실종자 신고 전화:</strong> 182 (실종아동찾기센터)</li>
                      <li><strong>경찰청 신고 전화:</strong> 112</li>
                      <li><strong>발신 기관:</strong> {extractAgency(selectedMessage.MSG_CN || selectedMessage.msg || '')}</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="close-button-footer" onClick={handleCloseDetails}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisasterMessagesList;